import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Share2, Link as LinkIcon, Mail, Copy, Check, Twitter, Facebook } from "lucide-react";
import { toast } from "sonner";

// WhatsApp icon (FontAwesome paths render cleaner than the lucide squircle)
const WhatsAppIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.6 6.32A7.85 7.85 0 0 0 12.04 4a7.94 7.94 0 0 0-6.78 12.06L4 20l4.05-1.06a7.92 7.92 0 0 0 3.99 1.02h.01a7.94 7.94 0 0 0 5.55-13.64Zm-5.55 12.21h-.01a6.6 6.6 0 0 1-3.36-.92l-.24-.14-2.4.63.64-2.34-.16-.25a6.6 6.6 0 0 1 10.27-8.16 6.6 6.6 0 0 1-4.74 11.18Zm3.61-4.94c-.2-.1-1.17-.58-1.36-.64-.18-.07-.31-.1-.45.1-.13.2-.51.64-.62.77-.12.13-.23.15-.43.05-.2-.1-.83-.31-1.59-.98-.59-.52-.99-1.18-1.1-1.38-.12-.2-.01-.31.09-.41.09-.09.2-.23.3-.35.1-.12.13-.2.2-.33.07-.13.03-.25-.02-.35-.05-.1-.45-1.08-.62-1.48-.16-.39-.33-.34-.45-.34l-.38-.01a.74.74 0 0 0-.53.25c-.18.2-.7.69-.7 1.67 0 .98.71 1.93.81 2.06.1.13 1.4 2.13 3.39 2.99.47.2.84.32 1.13.41.48.15.91.13 1.25.08.38-.06 1.17-.48 1.34-.94.16-.46.16-.86.11-.94-.05-.08-.18-.13-.38-.23Z"/>
  </svg>
);

/**
 * Reusable share button.
 *
 * Behaviour:
 * - On a device with the Web Share API (most mobile browsers + Safari) the
 *   button opens the native share sheet directly. That gets us iOS/Android
 *   parity with TikTok / IG without a custom drawer.
 * - On desktop (where Web Share is rare) we fall back to a dropdown with
 *   prebuilt deep-links to WhatsApp, X (Twitter), Facebook, email, and a
 *   copy-link option.
 * - The shared URL is always the canonical, tokenless film page so it works
 *   for non-logged-in viewers too.
 */
export default function ShareButton({
  url,
  title,
  text,
  variant = "outline",
  size = "default",
  className = "",
  iconOnly = false,
  testId = "share-button",
}) {
  const [copied, setCopied] = useState(false);

  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const shareTitle = title || "Cinevoya";
  const shareText = text || "I just found this on Cinevoya — short films from Africa, on demand.";

  const tryNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        return true;
      } catch (e) {
        // User cancelled the native sheet or it errored — fall through to dropdown.
        if (e?.name !== "AbortError") {
          // Real failure: log to console only; don't toast (noisy on cancel).
        }
      }
    }
    return false;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const externalLinks = [
    {
      name: "WhatsApp",
      icon: WhatsAppIcon,
      colour: "text-emerald-400",
      href: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
      testId: "share-whatsapp",
    },
    {
      name: "X (Twitter)",
      icon: Twitter,
      colour: "text-sky-300",
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      testId: "share-twitter",
    },
    {
      name: "Facebook",
      icon: Facebook,
      colour: "text-blue-400",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      testId: "share-facebook",
    },
    {
      name: "Email",
      icon: Mail,
      colour: "text-foreground/85",
      href: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`,
      testId: "share-email",
    },
  ];

  // Heuristic: on touch devices we get the native sheet; on desktop we open
  // the dropdown directly. We attach both behaviours: clicking the button
  // first attempts native, and if that's not available we open the menu.
  const handleClick = async (e) => {
    if (await tryNativeShare()) {
      e.preventDefault();
      e.stopPropagation();
    }
    // If not, the DropdownMenuTrigger's default behaviour opens the menu.
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant={variant}
          size={size}
          onClick={handleClick}
          className={`gap-1.5 ${className}`}
          aria-label="Share"
          data-testid={testId}
        >
          <Share2 className="w-4 h-4" />
          {!iconOnly && <span>Share</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-surface border-white/10 w-56" data-testid="share-menu">
        <DropdownMenuLabel className="text-xs text-foreground/55 font-normal">Share via</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        {externalLinks.map(({ name, icon: Icon, colour, href, testId: tid }) => (
          <DropdownMenuItem key={name} asChild className="cursor-pointer">
            <a href={href} target="_blank" rel="noopener noreferrer" data-testid={tid}>
              <Icon className={`w-4 h-4 mr-2 ${colour}`} /> {name}
            </a>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem onClick={copyLink} className="cursor-pointer" data-testid="share-copy-link">
          {copied
            ? <><Check className="w-4 h-4 mr-2 text-emerald-400" /> Link copied</>
            : <><LinkIcon className="w-4 h-4 mr-2" /> Copy link</>}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigator.clipboard.writeText(`${shareText} ${shareUrl}`).then(() => toast.success("Caption copied"))}
          className="cursor-pointer"
          data-testid="share-copy-caption"
        >
          <Copy className="w-4 h-4 mr-2" /> Copy caption + link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
